import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, catchError, concatMap, filter, from, map, mergeMap, of, switchMap, tap } from 'rxjs';

import { DataRepository } from '@core/data/data-repository';
import { PeopleApiService } from '@core/data/api/people-api.service';
import { peopleFromDto, personFromDto } from '@core/data/api/people.mapper';
import { PersonRow, PersonScheduledHours } from '@core/models';
import { apiErrorMessage } from '@core/util/api-error.util';

import { PeopleApiActions, PeoplePageActions } from './people.actions';

/** Loads the People directory roster from the data layer. */
@Injectable()
export class PeopleEffects {
  private readonly actions$ = inject(Actions);
  private readonly repo = inject(DataRepository);
  /** Training assignment is its own module's endpoint. */
  private readonly api = inject(PeopleApiService);

  // On page-enter, fetch the roster; switchMap cancels any in-flight load if the
  // page is re-entered. Dispatches loadSuccess on data, loadFailure on error.
  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PeoplePageActions.entered),
      switchMap(() =>
        this.roster$().pipe(
          map((people) => PeopleApiActions.loadSuccess({ people })),
          catchError((err: unknown) => of(PeopleApiActions.loadFailure({ error: toMessage(err) }))),
        ),
      ),
    ),
  );
  /**
   * A row was opened: read that person in full and merge them over the row.
   *
   * `EMPTY` without an API is right rather than lazy — the seeded roster already
   * carries everything the expanded panel shows, so there is nothing to fetch.
   * A failure is swallowed here because the interceptor has already shown it and
   * the row stays as it was; the panel is a read, so there is nothing half-written
   * to explain.
   */
  readonly loadPerson$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PeoplePageActions.personOpened),
      switchMap(({ code }) =>
        this.api.available()
          ? this.api.getPerson(code).pipe(
              map((dto) => PeopleApiActions.personLoaded({ person: personFromDto(dto) })),
              catchError(() => EMPTY),
            )
          : EMPTY,
      ),
    ),
  );

  /**
   * Somebody was just created: read the roster, THEN read them in full.
   *
   * In sequence, and that is the whole point. The create used to dispatch
   * `entered` and `personOpened` together, which race: the roster read REPLACES
   * the list and `personLoaded` is dropped for anybody not in it yet. Whichever
   * landed second decided whether the panel that opens on top of the new person
   * showed their trades, schedule and pay — or claimed they had none, which is
   * what it usually did, because the roster is the slower read.
   *
   * A failed record read still emits the roster: they exist, and a directory
   * missing the person just created is worse than a panel missing their detail.
   */
  readonly loadCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PeoplePageActions.personCreated),
      switchMap(({ code }) =>
        // READS ONLY. Adding somebody is ONE write, and it has already happened
        // — nothing else is sent here. A `PUT …/positions` briefly lived at this
        // point to save the seat the create cannot carry; it was a second write
        // that could fail once the person existed, so the three fields came off
        // the Add form instead (§8k). The create takes the seat the day
        // docs/backend-request-whole-person-one-call.md lands.
        this.roster$().pipe(
          switchMap((people) => {
            const roster = PeopleApiActions.loadSuccess({ people });
            if (!this.api.available()) return of([roster]);
            return this.api.getPerson(code).pipe(
              map((dto) => [roster, PeopleApiActions.personLoaded({ person: personFromDto(dto) })]),
              catchError(() => of([roster])),
            );
          }),
          concatMap((actions) => from(actions)),
          catchError((err: unknown) => of(PeopleApiActions.loadFailure({ error: toMessage(err) }))),
        ),
      ),
    ),
  );

  /**
   * People whose scheduled hours have been asked for — so each person's record
   * is read AT MOST ONCE however many screens (the nav badge, Today, My
   * Training, My Packet, My Team) ask. A failed read is forgotten, so a later
   * request tries again rather than leaving the failure standing forever.
   */
  private readonly scheduledHoursAsked = new Set<string>();

  /**
   * Somebody's scheduled hours: read their own record and hold the week.
   *
   * `mergeMap`, because different people are read side by side (My Team opens a
   * report while the nav badge reads the signed-in person) and one must not
   * cancel another.
   */
  readonly loadScheduledHours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PeoplePageActions.scheduledHoursRequested),
      filter(({ code }) => !!code && !this.scheduledHoursAsked.has(code)),
      tap(({ code }) => this.scheduledHoursAsked.add(code)),
      mergeMap(({ code }) =>
        this.scheduledHours$(code).pipe(
          map((scheduledHours) => PeopleApiActions.scheduledHoursLoaded({ code, scheduledHours })),
          catchError((err: unknown) => {
            this.scheduledHoursAsked.delete(code);
            return of(PeopleApiActions.scheduledHoursFailed({ code, error: apiErrorMessage(err) }));
          }),
        ),
      ),
    ),
  );

  /**
   * One person's scheduled hours. With an API, from their RECORD — the roster
   * row carries no week. Without one (the seeded demo) the seeded roster row
   * does carry it, so it is read from there. Somebody not on the seeded roster
   * has no hours, which is an answer, not a failure.
   */
  private scheduledHours$(code: string): Observable<PersonScheduledHours> {
    const toScheduledHours = (row: Partial<PersonRow> | undefined): PersonScheduledHours => ({
      blocks: row?.scheduledHours ?? [],
      lunchMinutes: row?.lunchMinutes ?? 0,
    });
    if (this.api.available()) return this.api.getPerson(code).pipe(map((dto) => toScheduledHours(personFromDto(dto))));
    return from(this.repo.listPeople()).pipe(map((rows) => toScheduledHours(rows.find((r) => r.id === code))));
  }

  /**
   * The roster from the API when one is configured, and the seeded repository when
   * not — the fallback is what keeps the prototype demoing without a backend.
   *
   * A roster row is deliberately thinner than the record: no pay, no address, no
   * trades, no scheduled hours. The directory fetches the record when a row is opened, so
   * what a list carries and what a person has are kept apart.
   */
  private roster$(): Observable<PersonRow[]> {
    if (!this.api.available()) return from(this.repo.listPeople());
    return this.api.listPeople().pipe(map(peopleFromDto));
  }
}

/** Normalize any thrown value to a display string for loadFailure. */
function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
